import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { firestoreDebugService, FirestoreLogEntry } from '@/services/firestoreDebugService';
import { formatDistanceToNow } from 'date-fns';

const FirestoreDebugSection: React.FC = () => {
  const [logs, setLogs] = useState<FirestoreLogEntry[]>([]);
  const [collectionMetrics, setCollectionMetrics] = useState<Map<string, any>>(new Map());
  const [componentMetrics, setComponentMetrics] = useState<Map<string, any>>(new Map());
  const [totalCounts, setTotalCounts] = useState<any>({});
  const [operationsPerMinute, setOperationsPerMinute] = useState<any>({});
  const [minutelyOperations, setMinutelyOperations] = useState<any[]>([]);
  const [isLoggingEnabled, setIsLoggingEnabled] = useState<boolean>(true);
  const [refreshInterval, setRefreshInterval] = useState<number | null>(null);
  const [selectedCollection, setSelectedCollection] = useState<string>('all');
  const [selectedComponent, setSelectedComponent] = useState<string>('all');
  const [selectedOperation, setSelectedOperation] = useState<string>('all');
  
  // Refresh data every second
  useEffect(() => {
    const interval = setInterval(() => {
      refreshData();
    }, 1000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Refresh data function
  const refreshData = () => {
    setLogs(firestoreDebugService.getLogs());
    setCollectionMetrics(firestoreDebugService.getCollectionMetrics());
    setComponentMetrics(firestoreDebugService.getComponentMetrics());
    setTotalCounts(firestoreDebugService.getTotalCounts());
    setOperationsPerMinute(firestoreDebugService.getOperationsPerMinute());
    setMinutelyOperations(firestoreDebugService.getMinutelyOperations());
    setIsLoggingEnabled(firestoreDebugService.isEnabled());
  };
  
  // Toggle logging
  const toggleLogging = () => {
    firestoreDebugService.setEnabled(!isLoggingEnabled);
    setIsLoggingEnabled(!isLoggingEnabled);
  };
  
  // Reset metrics
  const resetMetrics = () => {
    firestoreDebugService.reset();
    refreshData();
  };
  
  // Filter logs
  const filteredLogs = logs.filter(log => {
    if (selectedCollection !== 'all' && log.collection !== selectedCollection) return false;
    if (selectedComponent !== 'all' && (!log.component || !log.component.includes(selectedComponent))) return false;
    if (selectedOperation !== 'all' && log.operation !== selectedOperation) return false;
    return true;
  });
  
  // Get unique collections, components, and operations for filters
  const uniqueCollections = new Set<string>();
  const uniqueComponents = new Set<string>();
  const uniqueOperations = new Set<string>();
  
  logs.forEach(log => {
    uniqueCollections.add(log.collection);
    if (log.component) uniqueComponents.add(log.component);
    uniqueOperations.add(log.operation);
  });
  
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Firestore Debug</h3>
        <div className="space-x-2">
          <Button 
            variant={isLoggingEnabled ? "default" : "outline"} 
            className={isLoggingEnabled ? "bg-amber-600 hover:bg-amber-700 dark:bg-amber-600 dark:hover:bg-amber-700 dark:text-white" : "dark:text-white dark:border-gray-600"}
            onClick={toggleLogging}
          >
            {isLoggingEnabled ? 'Disable Logging' : 'Enable Logging'}
          </Button>
          <Button 
            variant="destructive" 
            className="dark:bg-red-600 dark:hover:bg-red-700 dark:text-white"
            onClick={resetMetrics}
          >
            Reset Metrics
          </Button>
        </div>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="dark:bg-gray-800 dark:border-gray-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium dark:text-white">Total Operations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold dark:text-white">{totalCounts.total || 0}</div>
            <div className="text-xs text-muted-foreground dark:text-gray-400">
              Since tracking started
            </div>
          </CardContent>
        </Card>
        
        <Card className="dark:bg-gray-800 dark:border-gray-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium dark:text-white">Reads/Minute</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold dark:text-white">{operationsPerMinute.reads || 0}</div>
            <div className="text-xs text-muted-foreground dark:text-gray-400">
              {totalCounts.reads || 0} total reads
            </div>
          </CardContent>
        </Card>
        
        <Card className="dark:bg-gray-800 dark:border-gray-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium dark:text-white">Writes/Minute</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold dark:text-white">{operationsPerMinute.writes || 0}</div>
            <div className="text-xs text-muted-foreground dark:text-gray-400">
              {totalCounts.writes || 0} total writes
            </div>
          </CardContent>
        </Card>
        
        <Card className="dark:bg-gray-800 dark:border-gray-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium dark:text-white">Deletes/Minute</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold dark:text-white">{operationsPerMinute.deletes || 0}</div>
            <div className="text-xs text-muted-foreground dark:text-gray-400">
              {totalCounts.deletes || 0} total deletes
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Tabs for different views */}
      <Tabs defaultValue="logs" className="w-full">
        <TabsList className="grid w-full grid-cols-3 dark:bg-gray-700">
          <TabsTrigger value="logs" className="dark:data-[state=active]:bg-gray-800 dark:data-[state=active]:text-white dark:text-gray-300">Logs</TabsTrigger>
          <TabsTrigger value="collections" className="dark:data-[state=active]:bg-gray-800 dark:data-[state=active]:text-white dark:text-gray-300">Collections</TabsTrigger>
          <TabsTrigger value="components" className="dark:data-[state=active]:bg-gray-800 dark:data-[state=active]:text-white dark:text-gray-300">Components</TabsTrigger>
        </TabsList>
        
        {/* Logs Tab */}
        <TabsContent value="logs" className="space-y-4">
          <Card className="dark:bg-gray-800 dark:border-gray-700">
            <CardHeader>
              <CardTitle className="dark:text-white">Firestore Operation Logs</CardTitle>
              <div className="flex flex-wrap gap-2 mt-2">
                <select 
                  className="p-2 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  value={selectedCollection}
                  onChange={(e) => setSelectedCollection(e.target.value)}
                >
                  <option value="all">All Collections</option>
                  {Array.from(uniqueCollections).map(collection => (
                    <option key={collection} value={collection}>{collection}</option>
                  ))}
                </select>
                
                <select 
                  className="p-2 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  value={selectedComponent}
                  onChange={(e) => setSelectedComponent(e.target.value)}
                >
                  <option value="all">All Components</option>
                  {Array.from(uniqueComponents).map(component => (
                    <option key={component} value={component}>{component}</option>
                  ))}
                </select>
                
                <select 
                  className="p-2 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  value={selectedOperation}
                  onChange={(e) => setSelectedOperation(e.target.value)}
                >
                  <option value="all">All Operations</option>
                  {Array.from(uniqueOperations).map(operation => (
                    <option key={operation} value={operation}>{operation}</option>
                  ))}
                </select>
              </div>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border dark:border-gray-700">
                <Table className="dark:text-gray-300">
                  <TableHeader className="dark:bg-gray-800">
                    <TableRow className="dark:border-gray-700">
                      <TableHead className="dark:text-gray-300">Time</TableHead>
                      <TableHead className="dark:text-gray-300">Operation</TableHead>
                      <TableHead className="dark:text-gray-300">Collection</TableHead>
                      <TableHead className="dark:text-gray-300">Document ID</TableHead>
                      <TableHead className="dark:text-gray-300">Component</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredLogs.slice(0, 100).map((log, index) => (
                      <TableRow key={index} className="dark:border-gray-700 dark:hover:bg-gray-700">
                        <TableCell className="dark:text-gray-300">{formatDistanceToNow(new Date(log.timestamp))} ago</TableCell>
                        <TableCell className="dark:text-gray-300">{log.operation}</TableCell>
                        <TableCell className="dark:text-gray-300">{log.collection}</TableCell>
                        <TableCell className="dark:text-gray-300">{log.docId || '-'}</TableCell>
                        <TableCell className="dark:text-gray-300">{log.component || 'unknown'}</TableCell>
                      </TableRow>
                    ))}
                    {filteredLogs.length === 0 && (
                      <TableRow className="dark:border-gray-700">
                        <TableCell colSpan={5} className="text-center dark:text-gray-300">No logs found</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              <div className="text-xs text-muted-foreground mt-2 dark:text-gray-400">
                Showing {Math.min(filteredLogs.length, 100)} of {filteredLogs.length} logs
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Collections Tab */}
        <TabsContent value="collections" className="space-y-4">
          <Card className="dark:bg-gray-800 dark:border-gray-700">
            <CardHeader>
              <CardTitle className="dark:text-white">Collection Metrics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border dark:border-gray-700">
                <Table className="dark:text-gray-300">
                  <TableHeader className="dark:bg-gray-800">
                    <TableRow className="dark:border-gray-700">
                      <TableHead className="dark:text-gray-300">Collection</TableHead>
                      <TableHead className="dark:text-gray-300">Reads</TableHead>
                      <TableHead className="dark:text-gray-300">Writes</TableHead>
                      <TableHead className="dark:text-gray-300">Deletes</TableHead>
                      <TableHead className="dark:text-gray-300">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Array.from(collectionMetrics.entries())
                      .sort(([, a], [, b]) => b.total - a.total)
                      .map(([collection, metrics]) => (
                        <TableRow key={collection} className="dark:border-gray-700 dark:hover:bg-gray-700">
                          <TableCell className="dark:text-gray-300">{collection}</TableCell>
                          <TableCell className="dark:text-gray-300">{metrics.reads}</TableCell>
                          <TableCell className="dark:text-gray-300">{metrics.writes}</TableCell>
                          <TableCell className="dark:text-gray-300">{metrics.deletes}</TableCell>
                          <TableCell className="dark:text-gray-300">{metrics.total}</TableCell>
                        </TableRow>
                      ))}
                    {collectionMetrics.size === 0 && (
                      <TableRow className="dark:border-gray-700">
                        <TableCell colSpan={5} className="text-center dark:text-gray-300">No collection metrics found</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Components Tab */}
        <TabsContent value="components" className="space-y-4">
          <Card className="dark:bg-gray-800 dark:border-gray-700">
            <CardHeader>
              <CardTitle className="dark:text-white">Component Metrics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border dark:border-gray-700">
                <Table className="dark:text-gray-300">
                  <TableHeader className="dark:bg-gray-800">
                    <TableRow className="dark:border-gray-700">
                      <TableHead className="dark:text-gray-300">Component</TableHead>
                      <TableHead className="dark:text-gray-300">Reads</TableHead>
                      <TableHead className="dark:text-gray-300">Writes</TableHead>
                      <TableHead className="dark:text-gray-300">Deletes</TableHead>
                      <TableHead className="dark:text-gray-300">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Array.from(componentMetrics.entries())
                      .sort(([, a], [, b]) => b.total - a.total)
                      .map(([component, metrics]) => (
                        <TableRow key={component} className="dark:border-gray-700 dark:hover:bg-gray-700">
                          <TableCell className="dark:text-gray-300">{component}</TableCell>
                          <TableCell className="dark:text-gray-300">{metrics.reads}</TableCell>
                          <TableCell className="dark:text-gray-300">{metrics.writes}</TableCell>
                          <TableCell className="dark:text-gray-300">{metrics.deletes}</TableCell>
                          <TableCell className="dark:text-gray-300">{metrics.total}</TableCell>
                        </TableRow>
                      ))}
                    {componentMetrics.size === 0 && (
                      <TableRow className="dark:border-gray-700">
                        <TableCell colSpan={5} className="text-center dark:text-gray-300">No component metrics found</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FirestoreDebugSection;
